import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import './JobDetails.less';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { jobsActions, jobsSelectors, jobsTypes } from '../../../../redux/jobs';
import Card from '../../../../components/envago/Card/Card';
import { splitJoin } from '../../../../helper/TextHelper';
import MeterHelper, { resolveMeterType, resolveUnitByObis, shortenObis, shouldShowObis } from '../../../../helper/MeterHelper';
import OptionalLabel from '../../../../components/envago/OptionalLabel/OptionalLabel';
import Toolbar from '../../../../components/envago/Toolbar/Toolbar';
import LabeledList from '../../../../components/envago/LabeledList/LabeledList';
import MeterReadingInput from '../../../../components/envago/MeterReadingInput/MeterReadingInput';
import MeterReadingUnit from '../../../../components/envago/MeterReadingUnit/MeterReadingUnit';
import Button from '../../../../components/envago/Button/Button';
import { dayjsTZ, formatDate, getLatest, isInFuture } from '../../../../helper/DateHelper';
import Icon from '../../../../components/envago/Icon/Icon';
import Information, { InformationDetails, InformationType } from '../../../../components/envago/Information/Information';
import MeterReadingTarif from '../../../../components/envago/MeterReadingTarif/MeterReadingTarif';
import { MeterRateTaskReadingResultType, MeterRateTaskType, MeterTaskResultType, ReadingEventType } from '../../../../redux/jobs/jobs.types';
import ShareJobDialog from '../../../common/ShareJobDialog/ShareJobDialog';
import PopConfirmButton from '../../../../components/envago/PopConfirmButton/PopConfirmButton';
import LoadingOverlay from '../../../../components/envago/LoadingOverlay/LoadingOverlay';
import { resolveJobDetailsState } from './resolveJobDetailsState';
import JobComment from '../../../../components/envago/JobComment/JobComment';
import BasicScreen from '../../../common/BasicScreen/BasicScreen';
import JobConstraintWarnings from './JobConstraintWarnings';
import MeloAttributeSection from './MeloAttributeSection';
import { resolveAvailableMeloAttributeTypes } from './meloAttributeTypes';
import MeloAttributeDialog, { MeloAttributeDialogMode } from './MeloAttributeDialog';
import { sortMeterRates } from '../../../../helper/Sorting';
import { Trans, useTranslation } from 'react-i18next';
import { Menu } from '@headlessui/react';
import MeterReadingJobHelp from '../MeterReadingJobHelp/MeterReadingJobHelp';
import SidePopover from '../../../../components/envago/SidePopover/SidePopover';
import { helpSelectors } from '../../../../redux/help';
import queueSelectors from '../../../../redux/offline/queue.selectors';
import { offlineTypes } from '../../../../redux/offline';
import DatePicker from '../../../../components/envago/DatePicker/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { sharesSelectors } from '../../../../redux/shares';
import { isBigger, isInRange as isInRangeHelper } from '../../../../helper/ResultHelper';
import { stringArraysAreEqual } from '../../../../helper/DataHelper';
import { hasVisibleField } from '../../../../fieldVisibility/fieldVisibility';
import { useFieldVisibility } from '../../../../fieldVisibility/useFieldVisibility';
import { readableMeterAddress } from '../../../../fieldVisibility/readableMeterAddress';
import { ApplicationConfig, MeterReadingConfigType } from '../../../../redux/application/application.types';
import { mergeJobConfig } from '../../../../helper/JobConfigHelper';
import { resolveDoneHintKey } from '../../../../helper/ReadingDateRangeHelper';
import { applicationSelectors } from '../../../../redux/application';
import { meloAttributeActions, meloAttributeSelectors } from '../../../../redux/meloAttribute';
import { push } from 'redux-first-history';
import { remoteResultActions } from '../../../../redux/remoteResult';
import { notificationActions } from '../../../../redux/notification';
import CommunicationJobDetailsSection from '../../../common/Communication/CommunicationJobDetailsSection';
import Collapsable from '../../../../components/envago/Collapsable/Collapsable';
import JobMeterRateTaskReadingImages, { MeterReadingPreviewImage } from './components/JobMeterRateTaskReadingImages';
import JobSuccessScreen from './components/JobSuccessScreen/JobSuccessScreen';
import ReadingValueLabel from './components/ReadingValueLabel/ReadingValueLabel';
import jobApi from '../../../../api/job.api';
import { loginSelectors } from '../../../../redux/login';
import { Helmet } from 'react-helmet-async';
import { useReduxActionListener } from '../../../../redux/useReduxActionListener';
import { RemoteResultActionPayloadByRemoteResultActionType } from '../../../../redux/remoteResult';
import { HorizontalBase64Image } from '../../../../components/envago/HorizontalBase64Image';
import MeterReadingTarifPlain from '../../../../components/envago/MeterReadingTarifPlain/MeterReadingTarifPlain';
import RemoteResultHelper from '../../../../helper/RemoteResultHelper';
import { isMobilePlatform } from '../../../../helper/PlatformHelper';
import { RemoteResult } from '../../../common/RemoteResultCommand/index.types';
import { resolveJobPhotoFlow, resolveReadingImagePrimary } from './jobPhotoFlow';
import JobDetailsReadingImageEntry from './components/JobDetailsReadingImageEntry';
import { useUnsavedChangesGuard } from '../../../common/UnsavedChangesGuard/UnsavedChangesGuard';
import { buildJobDetailsUnsavedChangesSnapshot, resolveAcceptedJobDetailsBaselineSnapshot, shouldGuardJobDetailsNavigation } from './JobDetails.unsavedChanges';
import { InvalidReadingImagePayloadError, toReadingImagePayload } from '../../../../helper/ReadingImagePayloadHelper';
import { transValue } from '../../../../helper/TransHelper';
import {
    getSubmitFeedbackStatusFromAction,
    shouldShowLegacyDoneView,
    shouldShowLegacyPendingView,
    shouldShowSubmitResultView,
    SubmitFeedbackStatus,
} from './JobDetails.submitFeedback';
import { SubmittedReadingSnapshot } from './components/JobSuccessScreen/JobSuccessScreen.helper';
import CapturedValuesSection from './components/CapturedValuesSection';
import {
    buildCapturedValuesPayload,
    CapturedValuesInputState,
    EMPTY_CAPTURED_VALUES_INPUT,
    isCapturedValuesSubmitBlocked,
    resolveCapturedValuesInputAfterReset,
    resolveCapturedValuesVisibility,
} from './JobDetails.capturedValues';
import { DEFAULT_PHONE_REGION } from './JobDetails.phoneRegion';

interface ResultStateValueType {
    lastReadingEvent?: ReadingEventType;
    lastReadingResult?: MeterRateTaskReadingResultType;
    readingValue?: string;
    preDecimals: number;
    postDecimals: number;
    valid: boolean;
    readingValueValidation: boolean | null;
    minValue: number;
    maxValue: number;
    showInvalidText: boolean;
    hasReadingValueValidationError: boolean;
    hasHistoricReadingValidationError: boolean;
    hasContinuousReadingValidationError: boolean;
    focus: boolean;
    strictValidation: boolean;
    images: Array<ImageWithRemoteResult>;
    isPostDecimalsMatched: boolean;
    isValidOrEmpty: boolean;
    isEmpty: boolean;
}

interface ImageWithRemoteResult {
    imageBase64: string;
    remoteResult: RemoteResultActionPayloadByRemoteResultActionType['REMOTE_RESULT_RECEIVED'];
}

interface ResultStateType {
    valid: boolean;
    partialValid: boolean;
    maskPostDecimals: boolean;
    queryPostDecimals: boolean;
    values: {
        [key: string]: ResultStateValueType;
    };
}

export const initialResultState: ResultStateType = {
    valid: false,
    partialValid: false,
    maskPostDecimals: false,
    queryPostDecimals: true,
    values: {},
};

export const resultReducer = (state: ResultStateType, action: any) => {
    switch (action.type) {
        case 'setFocus': {
            const { id, focus } = action;
            return {
                ...state,
                values: {
                    ...state.values,
                    [id]: {
                        ...state.values[id],
                        focus: focus,
                    },
                },
            };
        }
        case 'setConfig': {
            return {
                ...state,
                maskPostDecimals: action.payload.maskPostDecimals,
                queryPostDecimals: action.payload.queryPostDecimals,
            };
        }
        case 'setJobs': {
            let { job } = action;

            return {
                ...state,
                values: job.meter.meterRateTasks.reduce((res: any, mr: MeterRateTaskType) => {
                    res[mr.taskId] = {
                        lastReadingEvent:
                            (mr.readingEvents || []).length === 0
                                ? undefined
                                : mr.readingEvents?.reduce((prev, current) =>
                                      dayjs(prev.readingDate).valueOf() > dayjs(current.readingDate).valueOf() ? prev : current,
                                  ),
                        lastReadingResult:
                            (mr.readingResults || []).length === 0
                                ? undefined
                                : mr.readingResults?.reduce((prev, current) =>
                                      dayjs(prev.readingDate).valueOf() > dayjs(current.readingDate).valueOf() ? prev : current,
                                  ),
                        preDecimals: mr.preDecimals,
                        postDecimals: mr.postDecimals,
                        readingValueValidation: mr.readingValueValidation,
                        maxValue: mr.readingValueValidation?.maximumValue || Number.MAX_SAFE_INTEGER,
                        minValue: mr.readingValueValidation?.minimumValue || 0,
                        strictValidation: mr.readingValueValidation?.strictValidation,
                        valid: false,
                        showInvalidText: false,
                        hasReadingValueValidationError: false,
                        hasHistoricReadingValidationError: false,
                        hasContinuousReadingValidationError: false,
                        focus: false,
                        isPostDecimalsMatched: false,
                        isValidOrEmpty: true,
                        isEmpty: true,
                        images: [],
                    };
                    return res;
                }, {}),
            };
        }
        case 'setImages': {
            const { id, images } = action;
            return {
                ...state,
                values: {
                    ...state.values,
                    [id]: {
                        ...state.values[id],
                        images: images,
                    },
                },
            };
        }
        case 'setResult': {
            const { id, value } = action;
            const postDecimals = state.values[id].postDecimals;

            const valueNumber = Number.parseFloat(value);
            const enteredPostDecimals = value.split('.')[1]?.length || 0;
            const strictValidation = state.values[id].strictValidation;

            const isInRange =
                isInRangeHelper(valueNumber, state.values[id].minValue, state.values[id].maxValue, postDecimals) ||
                state.values[id].readingValueValidation === null;
            const isBiggerThanLastReadingEventReadingValue = isBigger(valueNumber, state.values[id].lastReadingEvent?.readingValue!, postDecimals);
            const isBiggerThanLastReadingResultReadingValue = isBigger(valueNumber, state.values[id].lastReadingResult?.readingValue!, postDecimals);

            const ignorePostDecimals = state.maskPostDecimals || !state.queryPostDecimals;

            const postDecimalsMatch = ignorePostDecimals || enteredPostDecimals === postDecimals || postDecimals === null;

            const isValid = isInRange && postDecimalsMatch;
            const isValidOrEmpty = isValid || value.length === 0;

            return {
                ...state,
                values: {
                    ...state.values,
                    [id]: {
                        ...state.values[id],
                        readingValue: value,
                        valid: strictValidation ? isValid : postDecimalsMatch,
                        showInvalidText: !postDecimalsMatch,
                        hasReadingValueValidationError: !isInRange,
                        hasHistoricReadingValidationError: !isBiggerThanLastReadingEventReadingValue,
                        hasContinuousReadingValidationError: !isBiggerThanLastReadingResultReadingValue,
                        isPostDecimalsMatched: postDecimalsMatch,
                        isValidOrEmpty: isValidOrEmpty,
                        isEmpty: value.length === 0,
                    },
                },
            };
        }
        case 'deleteResult': {
            const { id } = action;
            return {
                ...state,
                values: {
                    ...state.values,
                    [id]: {
                        ...state.values[id],
                        readingValue: undefined,
                        showInvalidText: false,
                        hasReadingValueValidationError: false,
                        hasHistoricReadingValidationError: false,
                        hasContinuousReadingValidationError: false,
                        valid: false,
                        isValidOrEmpty: true,
                        isEmpty: true,
                    },
                },
            };
        }
        case 'removeImage': {
            const { id, index } = action;
            return {
                ...state,
                values: {
                    ...state.values,
                    [id]: {
                        ...state.values[id],
                        images: state.values[id].images.filter((_, i) => i !== index),
                    },
                },
            };
        }
        case 'reset': {
            return initialResultState;
        }
    }
    return state;
};

export const computePartialValid = (values: ResultStateType['values']): boolean => {
    const anyValid = Object.values(values).some((v) => v.valid);
    const noPartialEntries = Object.values(values).every((v) => v.isEmpty || v.isPostDecimalsMatched);
    return anyValid && noPartialEntries;
};

const JobDetailsContent = ({ jobId }: { jobId: string; className?: string }) => {
    const dispatch = useDispatch();
    const store = useStore() as any;
    const { t } = useTranslation();

    // Factory selectors mint a fresh reselect instance with an empty cache on
    // every call. Wrapping in useMemo pins one selector per jobId so its cache
    // survives across renders.
    const jobByIdSelector = useMemo(() => jobsSelectors.getJobById(jobId), [jobId]);
    const job = useSelector(jobByIdSelector);

    const [readingResultImages, setReadingResultImages] = useState<Array<MeterReadingPreviewImage>>([]);

    const inputTimer = useRef<null | number>(null);
    const [showDelayedFeedback, setShowDelayedFeedback] = useState(false);
    const [isInputCollapseOpen, setIsInputCollapseOpen] = useState(!job?.done);
    const [submitFeedbackStatus, setSubmitFeedbackStatus] = useState<SubmitFeedbackStatus>('idle');
    const [submittedReadingSnapshot, setSubmittedReadingSnapshot] = useState<SubmittedReadingSnapshot | undefined>(undefined);

    const isSending = useSelector(jobsSelectors.isSending);
    const hasOtherOpenJobsSelector = useMemo(() => jobsSelectors.getHasOtherOpenJobs(jobId), [jobId]);
    const hasOtherOpenJobs = useSelector(hasOtherOpenJobsSelector);
    const showShareButtonSelector = useMemo(() => sharesSelectors.showShareButtonForJob(jobId), [jobId]);
    const showShareButton = useSelector(showShareButtonSelector);

    const applicationConfig: ApplicationConfig = useSelector(applicationSelectors.getApplicationConfig);
    const aiLiveAssistantEnabled = useSelector(applicationSelectors.isAiLiveAssistantEnabled);
    // Ohne Tabellenname: die Detailansicht kennt nur die portalweite Liste.
    const visibility = useFieldVisibility();
    const login = useSelector(loginSelectors.getLoginById(job.login));

    const meterReadingTarifWrapperWidth = useMemo(() => {
        // To set the min-width of the wrapper for the MeterReadingTarif div, estimate the maximum number of pixels needed for the longest word. The factor 13 is used because it has been tested as an ideal approximation (tested with expected words with one leading capital letter) for the length of one letter (NOTE: Uppercase approx. = 17, Lowercase approx. = 13) and to obtain the complete layout without overflow of the unit of measurement (kWh) on the right end. The additional 40 has to be added to compensate the margin of 1.5rem/40px from the children div.
        const meterRateTasksRateNameWordLengths = job.meter.meterRateTasks.map((mr) => {
            if (mr.rateName) {
                return mr.rateName.length;
            } else {
                return 0;
            }
        });
        return Math.min(280, Math.max(...meterRateTasksRateNameWordLengths) * 13 + 40);
    }, [job.meter.meterRateTasks]);

    const jobConfig = useMemo(() => {
        return mergeJobConfig(job.config, applicationConfig);
    }, [job, applicationConfig]);

    const meterReading: MeterReadingConfigType = useMemo(() => {
        const fallbackReadingDateMaxDaysInPast = job.jobConstraints?.earliestExecution
            ? getLatest(dayjsTZ(job.jobConstraints?.earliestExecution).startOf('day'), dayjsTZ().subtract(30, 'days').startOf('day'))
            : dayjsTZ().subtract(30, 'days').startOf('day');

        return {
            ...jobConfig?.meterReading,
            validationFeedbackDelay: jobConfig?.meterReading?.validationFeedbackDelay || 1500,
            maskPostDecimals: jobConfig?.meterReading?.maskPostDecimals ?? false,
            queryPostDecimals: jobConfig?.meterReading?.queryPostDecimals ?? false,
            showMaLo: jobConfig?.meterReading?.showMaLo ?? false,
            freeTextCommentEnabled: !jobConfig?.meterReading?.commentsEnabled ? false : jobConfig?.meterReading?.freeTextCommentEnabled ?? true,
            commentsEnabled: jobConfig?.meterReading?.commentsEnabled ?? false,
            readingDate: {
                readingDateMaxDaysInPast:
                    jobConfig?.meterReading.readingDate?.readingDateMaxDaysInPast ?? dayjsTZ().startOf('day').diff(fallbackReadingDateMaxDaysInPast, 'day'),
                readingDateMaxDaysInFuture: jobConfig?.meterReading.readingDate?.readingDateMaxDaysInFuture ?? 0,
            },
            aiImageEvaluationEnabled: jobConfig?.meterReading?.aiImageEvaluationEnabled ?? false,
            aiImageEvaluationAllowDesktop: jobConfig?.meterReading?.aiImageEvaluationAllowDesktop ?? false,
            aiImageEvaluationAllowGallery: jobConfig?.meterReading?.aiImageEvaluationAllowGallery ?? false,
            aiImageEvaluationMeterNumberMatchTrailingCharsCount: jobConfig?.meterReading?.aiImageEvaluationMeterNumberMatchTrailingCharsCount,
            aiImageEvaluationConfig: jobConfig?.meterReading?.aiImageEvaluationConfig,
        };
    }, [jobConfig, job]);

    const capturedValuesConfig = meterReading?.capturedValues;
    const capturedValuesVisibility = useMemo(
        () => resolveCapturedValuesVisibility({ config: capturedValuesConfig, job }),
        [capturedValuesConfig, job],
    );
    const capturedValuesDefaultRegion = capturedValuesConfig?.defaultPhoneRegion || DEFAULT_PHONE_REGION;

    const features = useMemo(() => {
        return {
            ...jobConfig?.features,
            imagesForbidden: jobConfig?.features?.imagesForbidden ?? false,
            imageAlwaysRequired: jobConfig?.features?.imagesForbidden ? false : jobConfig?.features?.imageAlwaysRequired ?? false,
            hideReadingDateInput: jobConfig?.features?.hideReadingDateInput ?? false,
            unplausibleReadingsCommentRequired: !meterReading.commentsEnabled ? false : jobConfig?.features?.unplausibleReadingsCommentRequired ?? false,
            unplausibleReadingsImageRequired: jobConfig?.features?.imagesForbidden ? false : jobConfig?.features?.unplausibleReadingsImageRequired ?? false,
            historicReadingsValidationRequired: jobConfig?.features?.historicReadingsValidationRequired ?? false,
            continuousReadingsValidationRequired: jobConfig?.features?.continuousReadingsValidationRequired ?? false,
            durationSendingReadingValuesBasedOnEarliestExecutionIsAllowed:
                jobConfig?.features?.durationSendingReadingValuesBasedOnEarliestExecutionIsAllowed ?? null,
            durationSendingReadingValuesBasedOnLatestExecutionIsAllowed:
                jobConfig?.features?.durationSendingReadingValuesBasedOnLatestExecutionIsAllowed ?? null,
            showReadingImagesAllowed: jobConfig?.features?.showReadingImagesAllowed ?? false,
            showReadingCommentAllowed: jobConfig?.features?.showReadingCommentAllowed ?? false,
            showPlausibilityAsText: jobConfig?.features?.showPlausibilityAsText ?? false,
            readingDateInputFixedToTargetReadingDate: jobConfig?.features?.readingDateInputFixedToTargetReadingDate ?? false,
            readingDateInputPreFilledToTargetReadingDate: jobConfig?.features?.readingDateInputPreFilledToTargetReadingDate ?? false,
        };
    }, [jobConfig, meterReading]);

    const photoFlow = useMemo(
        () =>
            resolveJobPhotoFlow({
                isMobile: isMobilePlatform,
                aiImageEvaluationEnabled: meterReading.aiImageEvaluationEnabled ?? false,
                aiImageEvaluationAllowDesktop: meterReading.aiImageEvaluationAllowDesktop ?? false,
                aiImageEvaluationAllowGallery: meterReading.aiImageEvaluationAllowGallery ?? false,
            }),
        [meterReading.aiImageEvaluationAllowDesktop, meterReading.aiImageEvaluationAllowGallery, meterReading.aiImageEvaluationEnabled],
    );

    const meterType = useMemo(() => {
        return resolveMeterType(job.meter);
    }, [job.meter]);

    const sampleObis = useMemo(() => {
        return job.meter.meterRateTasks.find((mrt) => mrt.obis)?.obis;
    }, [job]);

    const maxTargetReadingDate = useMemo(() => {
        const tasks = job.meter?.meterRateTasks ?? [];

        if (tasks.length === 0) {
            return dayjs().endOf('month');
        }

        const targetReadingDates = tasks?.map((mrt) => dayjs(mrt.targetReadingDate));
        const latest: Dayjs = targetReadingDates.reduce((prev, curr) => (curr.isAfter(prev) ? curr : prev));

        return latest;
    }, [job.meter?.meterRateTasks]);

    const [send, setSent] = useState(1);

    const [isPartialValid, setIsPartialValid] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [isCommentSetAndValid, setIsCommentSetAndValid] = useState(false);
    const [strictValidation, setStrictValidation] = useState(false);
    const [hasWarnings, setHasWarnings] = useState(false);
    const [shareDialogVisible, setShareDialogVisible] = useState(false);
    const [readingDate, setReadingDate] = useState(new Date());
    const [comment, setComment] = useState<string | undefined>(undefined);
    const [capturedValuesInput, setCapturedValuesInput] = useState<CapturedValuesInputState>(EMPTY_CAPTURED_VALUES_INPUT);
    const [showReadingHintRequiredWarning, setShowReadingHintRequiredWarning] = useState(false);
    const [baselineSnapshot, setBaselineSnapshot] = useState<string | undefined>(undefined);
    const submittedSnapshotRef = useRef<string | undefined>(undefined);

    const [resultState, dispatchResult] = useReducer(resultReducer, initialResultState);

    const isJobPending = useSelector(queueSelectors.isJobPending(job.id));

    const unsavedChangesSnapshot = useMemo(
        () =>
            buildJobDetailsUnsavedChangesSnapshot({
                readingDate,
                comment,
                resultState,
                capturedValues: capturedValuesInput,
            }),
        [comment, readingDate, resultState, capturedValuesInput],
    );
    const unsavedChangesSnapshotString = useMemo(() => JSON.stringify(unsavedChangesSnapshot), [unsavedChangesSnapshot]);
    const unsavedChangesSnapshotStringRef = useRef(unsavedChangesSnapshotString);
    unsavedChangesSnapshotStringRef.current = unsavedChangesSnapshotString;

    const hasInitializedUnsavedChangesBaseline = useMemo(() => {
        return !!job && Object.keys(resultState.values).length === job.meter.meterRateTasks.length;
    }, [job, resultState.values]);

    const hasUnsavedChanges = !!baselineSnapshot && baselineSnapshot !== unsavedChangesSnapshotString;
    const shouldGuardNavigation = shouldGuardJobDetailsNavigation({
        hasUnsavedChanges,
        isInputOpen: isInputCollapseOpen,
    });
    useUnsavedChangesGuard(shouldGuardNavigation, t('jobs.details.unsavedChanges.content'));

    const earliestExecution: { isValid: boolean; calculatedEarliestExecution?: string } = useMemo(() => {
        const earliestExecution = job.jobConstraints?.earliestExecution;

        if (!earliestExecution || features.durationSendingReadingValuesBasedOnEarliestExecutionIsAllowed === null) {
            return {
                isValid: true,
                calculatedEarliestExecution: undefined,
            };
        }

        const today = dayjs();
        const calculatedEarliestExecution = dayjs(earliestExecution).subtract(features.durationSendingReadingValuesBasedOnEarliestExecutionIsAllowed, 'days');

        let isValid = true;

        if (today.isBefore(calculatedEarliestExecution)) {
            isValid = false;
        }

        return {
            isValid: isValid,
            calculatedEarliestExecution: calculatedEarliestExecution.toISOString(),
        };
    }, [job, features.durationSendingReadingValuesBasedOnEarliestExecutionIsAllowed]);

    const showResultScreen = shouldShowSubmitResultView(submitFeedbackStatus);
    const showPendingScreen = submitFeedbackStatus === 'pending';
    const showDoneView = shouldShowLegacyDoneView({ jobDone: job.done, submitFeedbackStatus });
    const showLegacyPendingView = shouldShowLegacyPendingView({ isJobPending: !!isJobPending, jobDone: job.done, submitFeedbackStatus });

    const handleResend = () => {
        setSubmitFeedbackStatus('idle');
        setSubmittedReadingSnapshot(undefined);
        dispatchResult({ type: 'setJobs', job });
        setReadingDate(new Date());
        setComment(undefined);
        setCapturedValuesInput(resolveCapturedValuesInputAfterReset);
        setSent(send + 1);
        setBaselineSnapshot(undefined);
        setIsInputCollapseOpen(true);
        scrollElementIntoView(sortMeterRates(job.meter.meterRateTasks)?.[0]?.taskId);
    };

    const latestExecutionIsValid = useMemo(() => {
        if (job.jobConstraints == null || features.durationSendingReadingValuesBasedOnLatestExecutionIsAllowed === null) {
            return true;
        }

        const latestExecution = job.jobConstraints.latestExecution;

        if (latestExecution) {
            const today = dayjs();

            const calculatedLatestExecution = dayjs(latestExecution).add(features.durationSendingReadingValuesBasedOnLatestExecutionIsAllowed, 'days');

            if (today.isAfter(calculatedLatestExecution)) {
                return false;
            }
        }

        return true;
    }, [job, features.durationSendingReadingValuesBasedOnLatestExecutionIsAllowed]);

    const isInputVisible = useMemo(() => {
        return !(!earliestExecution.isValid || !latestExecutionIsValid);
    }, [earliestExecution.isValid, latestExecutionIsValid]);

    const hasAlwaysImageRequiredErrors = useMemo((): boolean => {
        if (!features?.imageAlwaysRequired) return false;

        return Object.values(resultState.values).reduce((res: boolean, v: any) => res || (!v.isEmpty && v.images.length === 0), false);
    }, [resultState, features?.imageAlwaysRequired]);

    const hasUnplausibleReadingsImageRequiredErrors = useMemo((): boolean => {
        if (!features?.unplausibleReadingsImageRequired) return false;

        return Object.values(resultState.values as { [key: string]: ResultStateValueType }).some((v) => {
            if (v.images?.length === 0 && v.isPostDecimalsMatched) {
                if (!v.isValidOrEmpty) {
                    return true;
                } else if (features?.historicReadingsValidationRequired && v.lastReadingEvent != null && v.hasHistoricReadingValidationError) {
                    return true;
                } else if (features.continuousReadingsValidationRequired && v.lastReadingResult != null && v.hasContinuousReadingValidationError) {
                    return true;
                }

                return false;
            }

            return false;
        });
    }, [
        resultState.values,
        features?.unplausibleReadingsImageRequired,
        features?.historicReadingsValidationRequired,
        features?.continuousReadingsValidationRequired,
    ]);

    const hasUnplausibleReadingsCommentRequiredErrors = useMemo((): boolean => {
        if (!features?.unplausibleReadingsCommentRequired) return false;

        return (
            !comment &&
            !isCommentSetAndValid &&
            Object.values(resultState.values as { [key: string]: ResultStateValueType }).some(
                (v) =>
                    !v.isValidOrEmpty ||
                    (features?.historicReadingsValidationRequired && v.lastReadingEvent != null && v.hasHistoricReadingValidationError) ||
                    (features?.continuousReadingsValidationRequired && v.lastReadingResult != null && v.hasContinuousReadingValidationError),
            )
        );
    }, [
        resultState,
        comment,
        isCommentSetAndValid,
        features?.unplausibleReadingsCommentRequired,
        features?.historicReadingsValidationRequired,
        features?.continuousReadingsValidationRequired,
    ]);

    const hasUnplausibleReadingsImageOrCommentRequiredErrors = useMemo(() => {
        if (!hasUnplausibleReadingsImageRequiredErrors && !hasUnplausibleReadingsCommentRequiredErrors) {
            return false;
        }

        if (features?.unplausibleReadingsImageRequired && features?.unplausibleReadingsCommentRequired) {
            if (hasUnplausibleReadingsImageRequiredErrors && hasUnplausibleReadingsCommentRequiredErrors) {
                return true;
            } else if (
                (!hasUnplausibleReadingsImageRequiredErrors && hasUnplausibleReadingsCommentRequiredErrors) ||
                (hasUnplausibleReadingsImageRequiredErrors && !hasUnplausibleReadingsCommentRequiredErrors)
            ) {
                return false;
            }
        }

        return hasUnplausibleReadingsImageRequiredErrors || hasUnplausibleReadingsCommentRequiredErrors;
    }, [
        hasUnplausibleReadingsImageRequiredErrors,
        hasUnplausibleReadingsCommentRequiredErrors,
        features?.unplausibleReadingsImageRequired,
        features?.unplausibleReadingsCommentRequired,
    ]);

    const sendButtonDisabled = useMemo(() => {
        if (
            hasAlwaysImageRequiredErrors ||
            hasUnplausibleReadingsImageOrCommentRequiredErrors ||
            (comment && !isCommentSetAndValid) ||
            isCapturedValuesSubmitBlocked({
                visibility: capturedValuesVisibility,
                phoneRequired: capturedValuesConfig?.phoneRequired === true,
                emailRequired: capturedValuesConfig?.emailRequired === true,
                input: capturedValuesInput,
                defaultPhoneRegion: capturedValuesDefaultRegion,
            })
        ) {
            return true;
        }

        return strictValidation ? !isValid : !isPartialValid;
    }, [
        strictValidation,
        isValid,
        isPartialValid,
        hasAlwaysImageRequiredErrors,
        hasUnplausibleReadingsImageOrCommentRequiredErrors,
        comment,
        isCommentSetAndValid,
        capturedValuesVisibility,
        capturedValuesConfig,
        capturedValuesInput,
        capturedValuesDefaultRegion,
    ]);

    // Der Sendeknopf trägt bei fehlenden Pflichtangaben ein natives `disabled` (Prop oben);
    // React unterdrückt `onClick` für deaktivierte Formularelemente von sich aus, ein Klick
    // darauf erzeugt also nie ein Ereignis, an dem sich "es wurde versucht abzusenden"
    // festmachen ließe. Der Pflichthinweis der Kontaktfelder soll aber trotzdem erscheinen,
    // sobald der Ableser sonst auf Senden drücken würde — also sobald die Ablesewerte selbst
    // fertig und gültig sind, unabhängig davon, ob die Kontaktfelder berührt wurden
    // (#930 Review, Befund 4).
    const capturedValuesSubmitAttempted = strictValidation ? isValid : isPartialValid;

    const hasInstructions = useSelector(helpSelectors.hasInstructions(job.meter.meterRateTasks.length, job.meter.meterNumber, sampleObis, job.meter.meterType));
    // Which rates show their obis next to the rate name (see shouldShowObis): electricity always
    // does — its jobs arrive split per register nowadays, and a bare "NZ" would not tell the
    // customer which one to read — while single-rate gas/water/heat show the rate name only.
    const displayObis = (mr: { obis?: string; rateName?: string }) =>
        shouldShowObis({ meterType: resolveMeterType(job.meter), meterRateCount: job.meter.meterRateTasks.length, rateName: mr.rateName }) ? mr.obis : undefined;

    const doneHint = useMemo(() => {
        const targetReadingDates = Object.values(job.meter.meterRateTasks).map((task) => dayjs(task.targetReadingDate).valueOf());
        const translationKey = resolveDoneHintKey(targetReadingDates, job.jobConstraints?.latestExecution);

        return (
            <Trans i18nKey={translationKey}>
                <b>title</b>
                <br />
                description
            </Trans>
        );
    }, [job]);

    // Die Entdopplung gegen den Kunden steckt in readableMeterAddress — eine
    // reine Funktion mit eigenen Tests, weil sie sonst nur per Rendertest
    // pruefbar waere.
    const meterAddressReadable = useMemo(
        () => readableMeterAddress(job.meter.meterAddress, job.customer, visibility),
        [job.meter.meterAddress, job.customer, visibility],
    );

    useEffect(() => {
        if (resultState?.values) {
            const valid = Object.values(resultState.values).reduce((res: boolean, v: any) => res && v.valid, true);
            if (valid !== isValid) {
                setIsValid(valid);
            }

            const partialValid = computePartialValid(resultState.values);
            if (partialValid !== isPartialValid) {
                setIsPartialValid(partialValid);
            }

            const commentsSetAndValid = comment !== undefined && comment.replace(/\s/g, '').length >= 5;

            if (commentsSetAndValid !== isCommentSetAndValid) {
                setIsCommentSetAndValid(commentsSetAndValid);
            }

            const warnings = Object.values(resultState.values).reduce((res: boolean, v: any) => res || v.warning, false);

            if (warnings !== hasWarnings) {
                setHasWarnings(warnings);
            }

            const showMissingHint = Object.values(resultState.values).reduce((res: boolean, v: any) => res || !v.isValidOrEmpty, false);

            const isAnyFocused = Object.values(resultState.values).reduce((res: boolean, v: any) => res || v.focus, false);

            const hasImages = Object.values(resultState.values).reduce((res: boolean, v: any) => res || v.images.length > 0, false);

            if (features?.unplausibleReadingsImageRequired && features?.unplausibleReadingsCommentRequired) {
                const showWarning =
                    features?.unplausibleReadingsCommentRequired &&
                    showMissingHint &&
                    features?.unplausibleReadingsImageRequired &&
                    !hasImages &&
                    !isAnyFocused &&
                    !comment;

                if (showWarning !== showReadingHintRequiredWarning) {
                    setShowReadingHintRequiredWarning(showWarning);
                }
            } else {
                const showWarning = features?.unplausibleReadingsCommentRequired && showMissingHint && !isAnyFocused && !comment;

                if (showWarning !== showReadingHintRequiredWarning) {
                    setShowReadingHintRequiredWarning(showWarning);
                }
            }
        }
    }, [resultState, features?.unplausibleReadingsCommentRequired, features?.unplausibleReadingsImageRequired, comment]);

    useEffect(() => {
        dispatchResult({
            type: 'setConfig',
            payload: {
                maskPostDecimals: meterReading?.maskPostDecimals,
                queryPostDecimals: meterReading?.queryPostDecimals,
            },
        });
    }, [jobId, meterReading?.maskPostDecimals, meterReading?.queryPostDecimals]);

    useEffect(() => {
        if (job) {
            dispatchResult({ type: 'setJobs', job });
        }

        setBaselineSnapshot(undefined);
        setIsInputCollapseOpen(!job?.done);
        setSubmitFeedbackStatus('idle');
        setSubmittedReadingSnapshot(undefined);
        submittedSnapshotRef.current = undefined;
        // Der Eingabezustand der Kontaktfelder lebt hier in der Komponente, nicht am Auftrag —
        // beim Wechsel zu einem anderen Auftrag (die Komponente bleibt gemountet, nur `jobId`
        // ändert sich) muss er deshalb explizit verworfen werden, sonst wandert eine noch nicht
        // abgesendete Telefonnummer/E-Mail-Adresse in den nächsten Auftrag (#930 Review, Befund 1).
        setCapturedValuesInput(resolveCapturedValuesInputAfterReset);

        return () => {
            dispatchResult({ type: 'reset' });
            setReadingDate(new Date());
            setBaselineSnapshot(undefined);
        };
    }, [jobId]);

    useEffect(() => {
        if (job) {
            const strict = job.meter.meterRateTasks.reduce((res, mr) => res || mr.readingValueValidation?.strictValidation || false, false);
            setStrictValidation(strict);
        }
    }, [job]);

    useEffect(() => {
        if (!features?.showReadingImagesAllowed || !job?.done || !login?.token) {
            return;
        }

        const abortController = new AbortController();

        const imageFetchPromises = job.meter.meterRateTasks.flatMap((mr) => {
            const lastReadingResult = mr.readingResults?.slice(-1)?.[0];

            if (lastReadingResult?.readingResultImages && lastReadingResult.readingResultImages.length > 0) {
                return lastReadingResult.readingResultImages.map((i) =>
                    jobApi.fetchReadingImagesForMeterRateTask(job.id, mr.taskId, i.imageId, login.token, abortController).then((res) => ({
                        taskId: mr.taskId,
                        imageId: i.imageId,
                        base64: res,
                    })),
                );
            }
            return [];
        });

        Promise.all(imageFetchPromises)
            .then((results) => {
                setReadingResultImages(
                    results.map((r, index) => ({
                        ...r,
                        index: index,
                    })),
                );
            })
            .catch((_error) => {
                // console.log('Error fetching readingImages: ', _error);
            });

        return () => {
            abortController.abort();
        };
    }, [job?.meter.meterRateTasks, login?.token, features?.showReadingImagesAllowed]);

    useEffect(() => {
        if (features?.readingDateInputPreFilledToTargetReadingDate) {
            setReadingDate(maxTargetReadingDate.toDate());
        }
    }, [maxTargetReadingDate, features?.readingDateInputPreFilledToTargetReadingDate]);

    useEffect(() => {
        if (!hasInitializedUnsavedChangesBaseline || baselineSnapshot) {
            return;
        }

        const baselineTimer = window.setTimeout(() => {
            setBaselineSnapshot(unsavedChangesSnapshotStringRef.current);
        }, 0);

        return () => {
            window.clearTimeout(baselineTimer);
        };
    }, [baselineSnapshot, hasInitializedUnsavedChangesBaseline, unsavedChangesSnapshotString]);

    useEffect(() => {
        const unsubscribe = store.subscribe(() => {
            const lastAction = store.lastAction;

            if (!submittedSnapshotRef.current || !lastAction) {
                return;
            }

            const nextSubmitFeedbackStatus = getSubmitFeedbackStatusFromAction(jobId, lastAction);

            if (nextSubmitFeedbackStatus) {
                setSubmitFeedbackStatus(nextSubmitFeedbackStatus);

                if (nextSubmitFeedbackStatus === 'error') {
                    setIsInputCollapseOpen(true);
                    submittedSnapshotRef.current = undefined;
                }
            }

            const submissionDone =
                lastAction.type === jobsTypes.SEND_METER_TASK_RESULT_DONE && lastAction.jobId === jobId;

            const submissionQueued =
                lastAction.type === offlineTypes.QUEUE_ACTION &&
                lastAction.action?.type === jobsTypes.SEND_METER_TASK_RESULTS &&
                lastAction.action?.jobId === jobId;

            const submissionAccepted = submissionDone || submissionQueued;

            if (!submissionAccepted) {
                return;
            }

            const capturedSnapshot = unsavedChangesSnapshotStringRef.current;
            window.setTimeout(() => {
                const acceptedBaselineSnapshot = resolveAcceptedJobDetailsBaselineSnapshot({
                    submissionAccepted: true,
                    currentSnapshot: capturedSnapshot,
                });

                setBaselineSnapshot(acceptedBaselineSnapshot);
                // Ref nur beim echten Erfolg clearen — bei QUEUE_ACTION muss der Ref erhalten
                // bleiben, damit ein späteres SEND_METER_TASK_RESULT_DONE (nach Sync) noch
                // verarbeitet werden kann und den Screen von 'pending' auf 'success' wechselt.
                if (submissionDone) {
                    submittedSnapshotRef.current = undefined;
                }
            }, 0);
        });

        return unsubscribe;
    }, [jobId, store]);

    // Nach dem Absenden einer Ablesung (Erfolg oder ausstehend/offline) wird der Ergebnis-Screen
    // angezeigt. Dann nach ganz oben scrollen, damit der "Zurück"-Button – insbesondere auf Mobile –
    // sichtbar ist. Der Effekt läuft nach dem DOM-Commit, sodass das Layout bereits steht; ein
    // requestAnimationFrame stellt sicher, dass auch der frisch gemountete Ergebnis-Screen gelayoutet
    // ist, bevor gescrollt wird (die bisherige Saga-Lösung scrollte zu früh und daher nur "halb").
    useEffect(() => {
        if (!showResultScreen) {
            return;
        }

        const raf = window.requestAnimationFrame(() => {
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        });

        return () => {
            window.cancelAnimationFrame(raf);
        };
    }, [showResultScreen]);

    const applicationName = useSelector(applicationSelectors.getApplicationName);
    const isMeloAttributeAllowed = useSelector(applicationSelectors.isMeloAttributeAllowed);
    const configuredMeloAttributeTypes = useSelector(applicationSelectors.getConfiguredMeloAttributeTypes);
    const availableMeloAttributeTypes = resolveAvailableMeloAttributeTypes(configuredMeloAttributeTypes);
    const meloAttributeSaving = useSelector(meloAttributeSelectors.isSaving);
    const meloAttributeHasConflict = useSelector(meloAttributeSelectors.hasConflict);
    const meloAttributeHasError = useSelector(meloAttributeSelectors.hasError);
    const meloAttributeIsStale = useSelector(meloAttributeSelectors.hasStale);
    const [meloDialogVisible, setMeloDialogVisible] = useState(false);
    const [meloDialogMode, setMeloDialogMode] = useState<MeloAttributeDialogMode>({ kind: 'create' });
    const canUseMeloAttributes = !showResultScreen && isMeloAttributeAllowed && !!job.meter?.meLoId && !job.meloAttributeSuppressed;

    // Eine leere Karte mit aria-label 'Stammdaten' waere fuer einen Screenreader
    // eine Gruppe ohne Inhalt und optisch ein leerer Kasten.
    //
    // Die Bedingung nennt zusaetzlich canUseMeloAttributes, weil dieselbe Card
    // auch MeloAttributeSection traegt: Vermerke stehen ausdruecklich NICHT im
    // Katalog und sind nicht ausblendbar — eine Bedingung nur auf hasMasterData
    // naehme sie still mit weg.
    const hasMasterData = hasVisibleField(visibility, [
        'meter.meterNumber',
        'meter.maLoId',
        'meter.meterType',
        'customer.customerNumber',
        'customer.summary',
        'partner.name',
        'meter.meterAddress.full',
        'meter.meterAddress.objectKey',
        'meter.meterAddress.locationDescription',
        'meter.meterAddress.hint',
    ]);
    const canCreateMeloAttribute = canUseMeloAttributes && availableMeloAttributeTypes.length > 0;

    useEffect(() => {
        dispatch(meloAttributeActions.resetStatus());
    }, [jobId]);

    const scrollElementIntoView = (id?: string) => {
        if (!id) {
            return;
        }

        setTimeout(() => {
            const element = document.querySelector(`[id*="${CSS.escape(id)}"]`);

            if (element) {
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start', // Aligns the top of the element with the top of the viewport
                });
            }
        }, 100);
    };

    const handleRemoteResultReceived = useCallback(
        (payload: RemoteResultActionPayloadByRemoteResultActionType['REMOTE_RESULT_RECEIVED']) => {
            // console.log('handleRemoteResultReceived PAYLOAD IN DETAILS :::', payload);

            if (
                !RemoteResultHelper.isMeterNumbersMatched(
                    job.meter.meterNumber,
                    payload.meterNumber,
                    meterReading.aiImageEvaluationMeterNumberMatchTrailingCharsCount,
                )
            ) {
                return;
            }

            // With a single detected rate we may safely fall back to the source task; with several
            // rates (e.g. analog HT/NT without OBIS) each rate must match its own task via OBIS or
            // obisRateName↔rateName, otherwise multiple values would collapse onto one task.
            const isSingleDetectedRate = (payload.meterRates?.length ?? 0) <= 1;

            payload.meterRates?.forEach((pmr) => {
                const matchedJobMeterRateTask: MeterRateTaskType | undefined = RemoteResultHelper.findMeterRateTaskForRate(
                    { obis: pmr.obis, obisRateName: pmr.obisRateName },
                    job.meter.meterRateTasks,
                    { sourceTaskId: payload.sourceTaskId, allowSourceTaskIdFallback: isSingleDetectedRate },
                );

                if (!matchedJobMeterRateTask) {
                    return;
                }

                const matchedTaskState = resultState.values[matchedJobMeterRateTask.taskId] || {
                    readingValue: undefined,
                    images: [],
                };

                const payloadReadingValue = pmr.readingValue;
                if (payloadReadingValue != null && matchedTaskState.readingValue !== payloadReadingValue) {
                    dispatchResult({
                        type: 'setResult',
                        id: matchedJobMeterRateTask.taskId,
                        value: payloadReadingValue,
                    });
                }

                if (pmr.readingImages?.length) {
                    const images: Array<ImageWithRemoteResult> = matchedTaskState.images ?? [];
                    const payloadImages = pmr.readingImages;

                    if (
                        !stringArraysAreEqual(
                            images.map((i) => i.imageBase64),
                            payloadImages,
                        )
                    ) {
                        dispatchResult({
                            type: 'setImages',
                            id: matchedJobMeterRateTask.taskId,
                            images: images.concat(
                                payloadImages.map((i) => ({
                                    imageBase64: i,
                                    remoteResult: payload,
                                })),
                            ),
                        });

                        scrollElementIntoView(matchedJobMeterRateTask.taskId);
                    }
                }
            });
        },
        [job, resultState.values],
    );

    useReduxActionListener<RemoteResultActionPayloadByRemoteResultActionType['REMOTE_RESULT_RECEIVED']>('REMOTE_RESULT_RECEIVED', handleRemoteResultReceived);

    if (!job) return null;

    return (
        <BasicScreen key={job.id}>
            <Helmet>
                <title>
                    {applicationName} | Ablesung {job.meter.customMeterType || t(`common.metertypes.${meterType}`)} {job.meter.meterNumber}
                </title>
            </Helmet>
            <ShareJobDialog jobs={[job]} visible={shareDialogVisible} onClose={setShareDialogVisible} />
            <Toolbar
                showBackButton={true}
                title={t('jobs.details.title')}
                toolbarButtons={
                    (canCreateMeloAttribute || showShareButton) && (
                        <>
                            {canCreateMeloAttribute && (
                                <Button
                                    className={'hidden sm:flex'}
                                    small={true}
                                    primary={false}
                                    ghost={true}
                                    onClick={() => {
                                        setMeloDialogMode({ kind: 'create' });
                                        setMeloDialogVisible(true);
                                    }}
                                    aria={{ 'aria-label': t('jobs.details.meloAttribute.addAria') }}
                                >
                                    <Icon path={Icon.Path.mdiPlus} aria={{ 'aria-hidden': true }} /> {t('jobs.details.meloAttribute.addLabel')}
                                </Button>
                            )}
                            {showShareButton && (
                                <Button
                                    className={'w-20'}
                                    small={true}
                                    primary={false}
                                    ghost={true}
                                    onClick={() => {
                                        setShareDialogVisible(true);
                                    }}
                                >
                                    <Icon aria={{ 'aria-label': 'Teilen Icon' }} path={Icon.Path.mdiShare} /> {t('jobs.details.buttons.share')}
                                </Button>
                            )}
                            {canCreateMeloAttribute && (
                                <Menu as={'div'} className={'relative sm:hidden my-1 -ml-1'}>
                                    <Menu.Button
                                        className={'flex items-center justify-center bg-transparent border-2 border-l-0 border-gray-200 rounded rounded-l-none py-2 px-1 text-gray-500 hover:border-gray-100 focus:outline-none'}
                                        aria-label={t('jobs.details.buttons.more')}
                                    >
                                        <Icon path={Icon.Path.mdiDotsVertical} className={'h-5 w-5'} aria={{ 'aria-hidden': true }} />
                                    </Menu.Button>
                                    <Menu.Items
                                        className={'origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white border border-gray-200 focus:outline-none z-10'}
                                    >
                                        <Menu.Item>
                                            {({ active }) => (
                                                <button
                                                    type={'button'}
                                                    onClick={() => {
                                                        setMeloDialogMode({ kind: 'create' });
                                                        setMeloDialogVisible(true);
                                                    }}
                                                    className={`${active ? 'bg-gray-100' : ''} flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700`}
                                                >
                                                    <Icon path={Icon.Path.mdiPlus} className={'h-4 w-4'} aria={{ 'aria-hidden': true }} /> {t('jobs.details.meloAttribute.addLabel')}
                                                </button>
                                            )}
                                        </Menu.Item>
                                    </Menu.Items>
                                </Menu>
                            )}
                        </>
                    )
                }
            />

            {isInputVisible && <JobConstraintWarnings constraints={job.jobConstraints} />}

            {!earliestExecution.isValid && earliestExecution.calculatedEarliestExecution && (
                <Information type={InformationType.INFO} className={'mb-2'}>
                    <InformationDetails
                        title={t('jobs.details.warnings.constraints.beforeSendingReadingValuesIsAllowedBasedOnEarliestExecutionAndDuration.title')}
                    >
                        <Trans
                            i18nKey={'jobs.details.warnings.constraints.beforeSendingReadingValuesIsAllowedBasedOnEarliestExecutionAndDuration.description'}
                            values={{ earliestExecution: formatDate(earliestExecution.calculatedEarliestExecution) }}
                        >
                            Der Zählerstand dieser Ablesung kann erst ab dem <b>{formatDate(earliestExecution.calculatedEarliestExecution)}</b> übermittelt
                            werden.
                            <br /> Bei Fragen wenden Sie sich bitte an den Kundenservice.
                        </Trans>
                    </InformationDetails>
                </Information>
            )}

            {!latestExecutionIsValid && (
                <Information type={InformationType.INFO} className={'mb-2'}>
                    <InformationDetails
                        title={t('jobs.details.warnings.constraints.afterSendingReadingValuesIsAllowedBasedOnLatestExecutionAndDuration.title')}
                    >
                        <Trans i18nKey={'jobs.details.warnings.constraints.afterSendingReadingValuesIsAllowedBasedOnLatestExecutionAndDuration.description'}>
                            Der Zählerstand dieser Ablesung kann leider nicht mehr übermittelt werden, da der Ablesezeitraum überschritten ist.
                            <br /> Bei Fragen wenden Sie sich bitte an den Kundenservice.
                        </Trans>
                    </InformationDetails>
                </Information>
            )}

            <SidePopover
                hidden={!hasInstructions}
                title={t('help.sidePopOver.title')}
                aria={{
                    role: 'complementary',
                    'aria-label': t('help.sidePopOver.title'),
                }}
                text={
                    <>
                        <Icon aria={{ 'aria-hidden': true }} path={Icon.Path.mdiHelpCircle} className={'h-6'} />
                        {t('help.sidePopOver.button')}
                    </>
                }
            >
                <MeterReadingJobHelp
                    numberOfMeterRates={job.meter.meterRateTasks.length}
                    meterNumber={job.meter.meterNumber}
                    obis={sampleObis}
                    meterType={job.meter.meterType}
                />
            </SidePopover>

            {!showResultScreen && (hasMasterData || canUseMeloAttributes) && (
                <Card aria={{ 'aria-label': 'Stammdaten', role: 'group' }}>
                    <LabeledList>
                        <OptionalLabel field={'meter.meterNumber'} label={t('jobs.details.labels.meterNumber')}>{job.meter.meterNumber}</OptionalLabel>

                        {/* showMaLo wirkt NEBEN dem field: der Schalter entscheidet, ob die
                            Marktlokation angeboten wird, die Config kann sie zusaetzlich
                            wegnehmen. Er hat die umgekehrte Polarität (Standard false) und
                            laesst sich deshalb nicht einschmelzen. */}
                        {meterReading.showMaLo && [job.meter.maLoId].filter(Boolean).length > 0 && (
                            <OptionalLabel field={'meter.maLoId'} label={t('jobs.details.labels.maLoId')}>
                                {[job.meter.maLoId].filter(Boolean).map((maLoId) => (
                                    <div key={maLoId}>{maLoId}</div>
                                ))}
                            </OptionalLabel>
                        )}

                        <OptionalLabel field={'meter.meterType'} label={t('jobs.details.labels.meterType')}>
                            {job.meter.customMeterType || t(`common.metertypes.${meterType}`)}
                        </OptionalLabel>

                        <OptionalLabel field={'customer.customerNumber'} label={t('jobs.details.labels.customerNumber')}>{job.customer?.customerNumber}</OptionalLabel>

                        <OptionalLabel field={'customer.summary'} label={t('jobs.details.labels.customer')}>
                            {splitJoin(
                                ', ',
                                visibility.pick('customer.company', job.customer?.company),
                                splitJoin(
                                    ' ',
                                    visibility.pick('customer.firstname', job.customer?.firstname),
                                    visibility.pick('customer.lastname', job.customer?.lastname),
                                ),
                            )}
                        </OptionalLabel>

                        <OptionalLabel field={'partner.name'} label={t('jobs.details.labels.partner')}>
                            {splitJoin(
                                ', ',
                                visibility.pick('partner.company', job.partner?.company),
                                splitJoin(
                                    ' ',
                                    visibility.pick('partner.firstname', job.partner?.firstname),
                                    visibility.pick('partner.lastname', job.partner?.lastname),
                                ),
                            )}
                        </OptionalLabel>

                        <OptionalLabel field={'meter.meterAddress.full'} label={t('jobs.details.labels.deliveryPoint')}>{meterAddressReadable}</OptionalLabel>

                        <OptionalLabel field={'meter.meterAddress.objectKey'} label={t('jobs.details.labels.objectKey')}>{job.meter.meterAddress?.objectKey}</OptionalLabel>

                        <OptionalLabel field={'meter.meterAddress.locationDescription'} label={t('jobs.details.labels.locationDescription')}>{job.meter.meterAddress?.locationDescription}</OptionalLabel>

                        <OptionalLabel field={'meter.meterAddress.hint'} label={t('jobs.details.labels.hint')}>{job.meter.meterAddress?.hint}</OptionalLabel>
                    </LabeledList>
                    {canUseMeloAttributes && (
                        <MeloAttributeSection
                            entries={job.meloAttributes ?? []}
                            onEdit={(item) => {
                                setMeloDialogMode({ kind: 'edit', item });
                                setMeloDialogVisible(true);
                            }}
                        />
                    )}
                </Card>
            )}

            {canUseMeloAttributes && (
                <MeloAttributeDialog
                    visible={meloDialogVisible}
                    mode={meloDialogMode}
                    availableTypes={availableMeloAttributeTypes}
                    saving={meloAttributeSaving}
                    errorMessage={meloAttributeIsStale ? t('jobs.details.meloAttribute.errors.stale') : meloAttributeHasConflict ? t('jobs.details.meloAttribute.errors.conflict') : meloAttributeHasError ? t('jobs.details.meloAttribute.errors.generic') : undefined}
                    onClose={() => setMeloDialogVisible(false)}
                    onCreate={(entry) => dispatch(meloAttributeActions.createMeloAttribute(job.login, job.meter.meLoId as string, entry))}
                    onUpdate={(id, entry, version) => dispatch(meloAttributeActions.updateMeloAttribute(job.login, job.meter.meLoId as string, id, entry, version))}
                    onDelete={(id) => dispatch(meloAttributeActions.deleteMeloAttribute(job.login, job.meter.meLoId as string, id))}
                />
            )}

            {showLegacyPendingView && (
                <Card className={'text-sm'}>
                    <Information type={InformationType.INFO}>
                        <p>
                            <Trans i18nKey={'jobs.details.pendingHint'}>
                                <b>Dieser Zähler wurde bereits abgelesen</b>
                                <br />
                                Das Ergebnis wird gesendet, sobald eine Internetverbindung besteht!
                            </Trans>
                        </p>
                    </Information>
                </Card>
            )}

            {showResultScreen && submittedReadingSnapshot && (
                <JobSuccessScreen
                    job={job}
                    readingResultImages={readingResultImages}
                    meterReading={meterReading}
                    features={features}
                    isPending={showPendingScreen}
                    hasOtherOpenJobs={hasOtherOpenJobs}
                    submittedReading={submittedReadingSnapshot}
                />
            )}

            {showDoneView && (
                <Card aria={{ role: 'group', 'aria-label': 'Erledigte Ablesung' }} className={'text-sm'}>
                    <Information type={InformationType.SUCCESS}>
                        <p>{doneHint}</p>
                        <CommunicationJobDetailsSection />
                    </Information>
                    <>
                        {sortMeterRates(job.meter.meterRateTasks).map((mr) => {
                            const expectedImageCount = mr.readingResults?.slice(-1)[0]?.readingResultImages?.length ?? 0;
                            return (
                                <Card key={mr.taskId} aria={{ 'aria-label': `Ablesetarif ${mr.rateName || mr.obis}`, role: 'group' }}>
                                    <LabeledList>
                                        <OptionalLabel label={t('jobs.details.labels.obis')}>
                                            {mr.rateName
                                                ? splitJoin(' ', mr.rateName, mr.obis && `(${shortenObis(mr.obis)})`)
                                                : mr.obis && `${shortenObis(mr.obis)}`}
                                        </OptionalLabel>
                                        <OptionalLabel label={t('jobs.details.labels.readingDate')}>
                                            {formatDate(mr.readingResults?.slice(-1)[0].readingDate)}
                                        </OptionalLabel>
                                        <ReadingValueLabel
                                            label={t('jobs.details.labels.readingValue')}
                                            value={mr.readingResults?.slice(-1)[0].readingValue}
                                            postDecimals={meterReading?.queryPostDecimals ? mr.postDecimals : 0}
                                            preDecimals={mr.preDecimals}
                                            maskPostDecimals={meterReading?.maskPostDecimals}
                                            unit={mr.unit}
                                            obis={mr.obis}
                                            maximumValue={mr.readingValueValidation?.maximumValue}
                                            minimumValue={mr.readingValueValidation?.minimumValue}
                                            plausibilityAsText={features.showPlausibilityAsText}
                                        />
                                        {features?.showReadingImagesAllowed && expectedImageCount > 0 && (
                                            <OptionalLabel label={t('jobs.details.labels.meterRateTaskReadingImages')} labelClassName={'self-start'}>
                                                <JobMeterRateTaskReadingImages taskId={mr.taskId} images={readingResultImages} expectedImageCount={expectedImageCount} />
                                            </OptionalLabel>
                                        )}
                                        {features.showReadingCommentAllowed && mr.readingResults?.slice(-1)[0].comment != null && (
                                            <OptionalLabel label={t('jobs.details.labels.meterRateTaskReadingComment')} labelClassName={'self-start'}>
                                                {mr.readingResults?.slice(-1)[0].comment}
                                            </OptionalLabel>
                                        )}
                                    </LabeledList>
                                </Card>
                            );
                        })}
                    </>
                </Card>
            )}

            {isInputVisible && (
                <LoadingOverlay loading={isSending} text={t('jobs.details.sending')}>
                    <Card>
                        <Collapsable
                            arrowPosition={'none'}
                            triggerText={
                                <Button
                                    className={'m-1'}
                                    block={true}
                                    accent={!showResultScreen}
                                    secondary={showResultScreen}
                                    aria={{
                                        tabIndex: 0,
                                        role: 'button',
                                    }}
                                    onClick={handleResend}
                                >
                                    {t('jobs.details.buttons.resendMeterReading', { count: job?.meter?.meterRateTasks?.length })}
                                </Button>
                            }
                            open={isInputCollapseOpen}
                            hideTriggerTextIfOpen={true}
                        >
                            {/* Camera-guided assistant entry right above the manual capture.
                                Living INSIDE the collapsable keeps it available whenever the
                                capture is: on open jobs (collapse starts open) AND on done jobs
                                after "Zählerstand erneut mitteilen" expanded the inputs. */}
                            {aiLiveAssistantEnabled && isMobilePlatform && (
                                <Button className={'mb-3'} secondary={true} block={true} onClick={() => dispatch(push('/ai-ablesung', { from: `/ablesung/${jobId}` }))}>
                                    <Icon path={Icon.Path.mdiCreation} /> {t('aiReading.buttons.startAssistant')}
                                </Button>
                            )}
                            <>
                                {job.meter.meterRateTasks.length > 1 && (
                                    <Information className={'mb-3 text-sm'}>
                                        <Trans i18nKey={'jobs.details.multiObisWarning'} values={{ meterRateCount: job.meter.meterRateTasks.length }}>
                                            {'Dieser Zähler hat '}
                                            <span className={'font-medium'}>{transValue({ meterRateCount: job.meter.meterRateTasks.length })} Tarife</span>
                                            {': Bitte beachte die Tarif-Bezeichnungen!'}
                                        </Trans>
                                    </Information>
                                )}
                            </>
                            <LabeledList>
                                <>
                                    {sortMeterRates(job.meter.meterRateTasks).map((mr, idx, self) => {
                                        const mrtValues = resultState.values[mr.taskId];

                                        const hasAnyValidationError =
                                            !mrtValues?.isValidOrEmpty ||
                                            (features?.historicReadingsValidationRequired &&
                                                mrtValues?.lastReadingEvent != null &&
                                                mrtValues?.hasHistoricReadingValidationError) ||
                                            (features?.continuousReadingsValidationRequired &&
                                                mrtValues?.lastReadingResult != null &&
                                                mrtValues?.hasContinuousReadingValidationError);

                                        const showImageRequiredError =
                                            features?.unplausibleReadingsImageRequired && mrtValues?.images.length === 0 && hasAnyValidationError;

                                        const showCommentRequiredError =
                                            features?.unplausibleReadingsCommentRequired &&
                                            !(comment && comment.length > 0 && isCommentSetAndValid) &&
                                            hasAnyValidationError;

                                        const showImageOrCommentRequiredError = showImageRequiredError && showCommentRequiredError;

                                        const isFeedInMeter = mr.obis?.includes(':2.8');
                                        const lastHistoricReadingEvent = mrtValues?.lastReadingEvent;
                                        const unit = mr.unit || resolveUnitByObis(mr?.obis);
                                        const interpolationParams = {
                                            historicReadingValue: lastHistoricReadingEvent != null ? lastHistoricReadingEvent.readingValue : undefined,
                                            unit: lastHistoricReadingEvent != null ? unit : undefined,
                                            historicReadingDate:
                                                lastHistoricReadingEvent != null ? dayjs(lastHistoricReadingEvent.readingDate).format('DD.MM.YYYY') : undefined,
                                        };

                                        const showHistoricReadingsValidationWarning =
                                            features?.historicReadingsValidationRequired &&
                                            mrtValues?.lastReadingEvent != null &&
                                            mrtValues?.hasHistoricReadingValidationError;

                                        const showContinuousReadingsValidationWarning =
                                            features?.continuousReadingsValidationRequired &&
                                            mrtValues?.lastReadingResult != null &&
                                            mrtValues?.hasContinuousReadingValidationError;

                                        // The reading is only implausible because it is lower than the previous
                                        // reading — name that reason instead of the generic "not plausible".
                                        const isContinuousOnlyValidationError =
                                            showContinuousReadingsValidationWarning &&
                                            mrtValues?.isValidOrEmpty &&
                                            !showHistoricReadingsValidationWarning;

                                        let errorText = undefined;
                                        if (mrtValues?.showInvalidText) {
                                            errorText = t('jobs.details.warnings.invalidText', {
                                                count: mr.postDecimals,
                                            });
                                        } else if (showImageRequiredError && !features?.unplausibleReadingsCommentRequired) {
                                            if (isContinuousOnlyValidationError) {
                                                errorText = t('jobs.details.warnings.continuousValidationImageRequiredError', interpolationParams);
                                            } else if (isFeedInMeter) {
                                                errorText = t('jobs.details.warnings.imageRequiredFeedInMeterError', interpolationParams);
                                            } else {
                                                errorText = t('jobs.details.warnings.imageRequiredError', interpolationParams);
                                            }
                                        } else if (showImageOrCommentRequiredError) {
                                            if (isContinuousOnlyValidationError) {
                                                errorText = t('jobs.details.warnings.continuousValidationImageOrCommentRequiredError', interpolationParams);
                                            } else if (isFeedInMeter) {
                                                errorText = t('jobs.details.warnings.imageOrCommentRequiredFeedInMeterError', interpolationParams);
                                            } else {
                                                errorText = t('jobs.details.warnings.imageOrCommentRequiredError', interpolationParams);
                                            }
                                        }

                                        let warningText = undefined;
                                        if (showContinuousReadingsValidationWarning) {
                                            warningText = t('jobs.details.warnings.continuousReadingsValidationWarningText', interpolationParams);
                                        } else if (showHistoricReadingsValidationWarning) {
                                            if (isFeedInMeter) {
                                                warningText = t('jobs.details.warnings.historicValidationFeedInMeterWarningText', interpolationParams);
                                            } else {
                                                warningText = t('jobs.details.warnings.historicValidationWarningText', interpolationParams);
                                            }
                                        } else if (mrtValues?.hasReadingValueValidationError) {
                                            warningText = t('jobs.details.warnings.validationWarningText', interpolationParams);
                                        }

                                        const hasFocus = mrtValues?.focus;
                                        const showFeedback: boolean = !hasFocus || showDelayedFeedback;

                                        const meterRateTask = job.meter.meterRateTasks?.find((mrt) => mrt.obis === mr.obis);

                                        const images: Array<ImageWithRemoteResult> = resultState?.values[mr.taskId]?.images || [];
                                        const readingImagePrimary = resolveReadingImagePrimary({
                                            useAi: photoFlow.useAi,
                                            hasReadingValue: resultState?.values[mr.taskId]?.readingValue != null,
                                            hasImages: images.length > 0,
                                            imageRequired: hasAlwaysImageRequiredErrors || hasUnplausibleReadingsImageRequiredErrors,
                                        });
                                        const latestRemoteResult = images?.[images?.length - 1]?.remoteResult;
                                        // Prefer this tariff's own display crop (multi-tariff/analog HT/NT); fall back to the shared crop.
                                        const latestDisplayCrop =
                                            (latestRemoteResult?.meterRates &&
                                                RemoteResultHelper.findRateForMeterRateTask(
                                                    { obis: mr.obis, rateName: mr.rateName },
                                                    latestRemoteResult.meterRates,
                                                )?.detectedDisplayCropBase64) ||
                                            latestRemoteResult?.detectedDisplayCropBase64;

                                        const effectivePreDecimals = meterRateTask?.preDecimals || 8;
                                        const effectivePostDecimals = meterReading?.queryPostDecimals ? mr.postDecimals || 0 : 0;
                                        const otherMeterRates = self
                                            .filter((s) => s.taskId !== mr.taskId)
                                            .map((s) => ({
                                                rateName: s.rateName,
                                                obis: s.obis,
                                                preDecimals: effectivePreDecimals,
                                                postDecimals: effectivePostDecimals,
                                                minValue: s.readingValueValidation?.minimumValue,
                                                maxValue: s.readingValueValidation?.maximumValue,
                                            }));

                                        const meterType = MeterHelper.resolveMeterType(job.meter);

                                        const aiImageEvaluationConfig = meterReading.aiImageEvaluationConfig;
                                        const aiImageEvaluationConfigByMeterType = aiImageEvaluationConfig?.overrideByMeterType?.[meterType];
                                        const padReadingValuePostDecimalsEnabled =
                                            aiImageEvaluationConfigByMeterType?.padReadingValuePostDecimalsEnabled != null
                                                ? aiImageEvaluationConfigByMeterType.padReadingValuePostDecimalsEnabled
                                                : meterReading.aiImageEvaluationConfig?.padReadingValuePostDecimalsEnabled || false;

                                        const aiReadingImageConfig = {
                                            sourceTaskId: mr.taskId,
                                            accountId: job.login,
                                            meterNumber: job.meter.meterNumber,
                                            meterNumberMatchTrailingCharsCount: meterReading.aiImageEvaluationMeterNumberMatchTrailingCharsCount,
                                            padReadingValuePostDecimalsEnabled: padReadingValuePostDecimalsEnabled,
                                            sourceMeterRate: {
                                                rateName: meterRateTask?.rateName,
                                                obis: meterRateTask?.obis,
                                                preDecimals: effectivePreDecimals, // fallback to 8, same as input
                                                postDecimals: effectivePostDecimals,
                                                minValue: meterRateTask?.readingValueValidation?.minimumValue,
                                                maxValue: meterRateTask?.readingValueValidation?.maximumValue,
                                            },
                                            otherMeterRates: otherMeterRates,
                                        };

                                        const handleStandardImageChange = (newImages: string[] | undefined) => {
                                            dispatchResult({
                                                type: 'setImages',
                                                id: mr.taskId,
                                                images: newImages?.map((i) => ({
                                                    imageBase64: i,
                                                    remoteResult: undefined,
                                                })),
                                            });
                                        };

                                        const handleRemoveImage = (index: number) => {
                                            dispatchResult({ type: 'removeImage', id: mr.taskId, index });
                                        };

                                        const handleAiResultReceived = (result: RemoteResult | undefined, imageBase64: string) => {
                                            const payload = result
                                                ? {
                                                      ...RemoteResultHelper.mapRemoteResultToPayload(result, [imageBase64], aiReadingImageConfig),
                                                      sourceTaskId: mr.taskId,
                                                  }
                                                : undefined;

                                            if (payload && RemoteResultHelper.canAssignResultToMeterRateTasks(payload, job.meter.meterRateTasks, mr.taskId)) {
                                                dispatch(remoteResultActions.remoteResultReceivedAction(payload));
                                                return;
                                            }

                                            // No result, or none of its rates fits a task (e.g. "take photo" although the AI read
                                            // no rate or a meter of another medium): the photo still belongs to this task.
                                            dispatchResult({
                                                type: 'setImages',
                                                id: mr.taskId,
                                                images: [
                                                    ...(resultState?.values[mr.taskId]?.images ?? []),
                                                    {
                                                        imageBase64,
                                                        remoteResult: payload,
                                                    },
                                                ],
                                            });
                                        };

                                        return (
                                            <React.Fragment key={`${mr.taskId}-${send}`}>
                                                <div
                                                    className={`relative flex mb-2 items-center col-span-2 ${idx === 0 ? 'mt-2' : 'mt-8'}`}
                                                    id={mr.taskId}
                                                    style={{
                                                        scrollMarginTop: 72,
                                                    }}
                                                >
                                                    <div className="flex-grow border-t border-gray-400"></div>
                                                    {(mr.rateName || mr?.obis) && (
                                                        <>
                                                            <span className="flex-shrink mx-4 text-gray-500">
                                                                <MeterReadingTarifPlain rateName={mr.rateName} obis={displayObis(mr)} />
                                                            </span>
                                                            <div className="flex-grow border-t border-gray-400"></div>
                                                        </>
                                                    )}
                                                </div>
                                                {!features?.imagesForbidden && photoFlow.useAi && (
                                                    <OptionalLabel label={t('jobs.details.labels.readingImage')} className={'w-full min-w-0'}>
                                                        <>
                                                            <JobDetailsReadingImageEntry
                                                                flow={photoFlow}
                                                                config={aiReadingImageConfig}
                                                                images={images?.map((i) => i.imageBase64)}
                                                                primary={readingImagePrimary}
                                                                onStandardChange={(newImages) => {
                                                                    handleStandardImageChange(newImages);
                                                                    scrollElementIntoView(mr.taskId);
                                                                }}
                                                                onResultReceived={(result, imageBase64) => {
                                                                    handleAiResultReceived(result, imageBase64);
                                                                    scrollElementIntoView(mr.taskId);
                                                                }}
                                                                text={t('jobs.details.buttons.uploadImage')}
                                                                onDelete={handleRemoveImage}
                                                                deleteDisabled={isSending}
                                                            />
                                                            {latestDisplayCrop && (
                                                                <div
                                                                    id={`${mr.taskId}-detected-display-crop`}
                                                                    style={{
                                                                        scrollMarginTop: 72,
                                                                    }}
                                                                    className={'w-full border rounded px-2 py-1 bg-gray-100 mt-2'}
                                                                >
                                                                    <HorizontalBase64Image
                                                                        src={latestDisplayCrop}
                                                                        alt={'Bildausschnitt vom Display'}
                                                                        className={'rounded'}
                                                                    />
                                                                </div>
                                                            )}
                                                        </>
                                                    </OptionalLabel>
                                                )}
                                                <OptionalLabel label={t('jobs.details.labels.reading')}>
                                                    <div className={'flex items-center flex-wrap xl:flex-nowrap'}>
                                                        <div
                                                            aria-label={'Zählerstandseingabe'}
                                                            role={'group'}
                                                            className={'w-full flex items-center md:w-max md:justify-start justify-center'}
                                                        >
                                                            <div
                                                                className={'break-all'}
                                                                style={{
                                                                    width: meterReadingTarifWrapperWidth,
                                                                    minWidth: 100,
                                                                }}
                                                            >
                                                                <MeterReadingTarif obis={displayObis(mr)} rateName={mr.rateName} />
                                                            </div>

                                                            <MeterReadingInput
                                                                maskPostDecimals={meterReading?.maskPostDecimals}
                                                                preDecimals={effectivePreDecimals}
                                                                postDecimals={effectivePostDecimals}
                                                                key={`${mr.taskId}-${send}-${effectivePreDecimals}-${effectivePostDecimals}-${
                                                                    meterReading?.maskPostDecimals ? 1 : 0
                                                                }`}
                                                                value={resultState?.values[mr.taskId]?.readingValue}
                                                                onFocusChange={(focus) => {
                                                                    if (!focus) {
                                                                        setShowDelayedFeedback(false);
                                                                    }

                                                                    dispatchResult({
                                                                        type: 'setFocus',
                                                                        focus: focus,
                                                                        id: mr.taskId,
                                                                    });
                                                                }}
                                                                onChange={(value) => {
                                                                    if (value && value.length > 0) {
                                                                        if (inputTimer.current) {
                                                                            clearTimeout(inputTimer.current);
                                                                            setShowDelayedFeedback(false);
                                                                        }

                                                                        inputTimer.current = setTimeout(() => {
                                                                            setShowDelayedFeedback(true);
                                                                        }, meterReading?.validationFeedbackDelay);

                                                                        dispatchResult({
                                                                            type: 'setResult',
                                                                            id: mr.taskId,
                                                                            value,
                                                                        });
                                                                    } else {
                                                                        dispatchResult({
                                                                            type: 'deleteResult',
                                                                            id: mr.taskId,
                                                                        });
                                                                    }
                                                                }}
                                                            />

                                                            <MeterReadingUnit className={'text-gray-600'} obis={mr.obis} unit={mr.unit} />

                                                            {showFeedback &&
                                                                mrtValues?.valid &&
                                                                !(
                                                                    mrtValues?.showInvalidText ||
                                                                    mrtValues?.hasReadingValueValidationError ||
                                                                    showHistoricReadingsValidationWarning ||
                                                                    showContinuousReadingsValidationWarning
                                                                ) && (
                                                                    <div className={'lg:ml-3 relative'}>
                                                                        <Icon
                                                                            aria={{ 'aria-label': 'Haken OK' }}
                                                                            path={Icon.Path.mdiCheck}
                                                                            className={'h-6 text-green-500 absolute left-1 -top-3'}
                                                                        />
                                                                    </div>
                                                                )}
                                                        </div>
                                                        <div className={'text-sm lg:ml-3 xl:mt-0 mt-2'}>
                                                            {showFeedback && (
                                                                <>
                                                                    <Information type={InformationType.ERROR}>{errorText}</Information>
                                                                    {!errorText && (
                                                                        <Information type={strictValidation ? InformationType.ERROR : InformationType.WARNING}>
                                                                            {warningText}
                                                                        </Information>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </OptionalLabel>
                                                {!features?.imagesForbidden && !photoFlow.useAi && (
                                                    <OptionalLabel label={t('jobs.details.labels.readingImage')} className={'w-full min-w-0'}>
                                                        <JobDetailsReadingImageEntry
                                                            flow={photoFlow}
                                                            config={aiReadingImageConfig}
                                                            images={images.map((i) => i.imageBase64)}
                                                            primary={readingImagePrimary}
                                                            onStandardChange={(newImages) => {
                                                                handleStandardImageChange(newImages);
                                                            }}
                                                            onResultReceived={(result, imageBase64) => {
                                                                handleAiResultReceived(result, imageBase64);
                                                            }}
                                                            text={t('jobs.details.buttons.uploadImage')}
                                                            onDelete={handleRemoveImage}
                                                            deleteDisabled={isSending}
                                                        />
                                                    </OptionalLabel>
                                                )}

                                                {!job.sameTargetReadingDates && (
                                                    <OptionalLabel label={t('jobs.details.labels.targetReadingDate')}>
                                                        {formatDate(mr.targetReadingDate)}
                                                    </OptionalLabel>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </>
                                <hr className={'col-span-2 my-5 border-gray-400'} />

                                {!features?.hideReadingDateInput &&
                                    (!(job.jobConstraints?.earliestExecution && isInFuture(job.jobConstraints?.earliestExecution)) ||
                                        !(earliestExecution.calculatedEarliestExecution && isInFuture(earliestExecution.calculatedEarliestExecution))) && (
                                        <OptionalLabel label={t('jobs.details.labels.readingDate')}>
                                            <DatePicker
                                                value={features.readingDateInputFixedToTargetReadingDate ? maxTargetReadingDate.toDate() : readingDate}
                                                readonly={features.readingDateInputFixedToTargetReadingDate ?? false}
                                                minDate={dayjsTZ()
                                                    .subtract(meterReading.readingDate?.readingDateMaxDaysInPast || 0, 'days')
                                                    .startOf('day')
                                                    .toDate()}
                                                maxDate={dayjsTZ()
                                                    .add(meterReading.readingDate?.readingDateMaxDaysInFuture || 0, 'days')
                                                    .startOf('day')
                                                    .toDate()}
                                                includedDates={
                                                    features.readingDateInputPreFilledToTargetReadingDate ? [maxTargetReadingDate.toDate()] : undefined
                                                }
                                                onChange={setReadingDate}
                                            />
                                        </OptionalLabel>
                                    )}
                                {meterReading.commentsEnabled && (
                                    <OptionalLabel label={t('jobs.details.labels.readingHint')}>
                                        <JobComment
                                            dropdownPosition={'relative'}
                                            primary={
                                                !(hasAlwaysImageRequiredErrors || hasUnplausibleReadingsImageRequiredErrors) &&
                                                hasUnplausibleReadingsImageOrCommentRequiredErrors
                                            }
                                            additionalComments={meterReading?.defaultComments}
                                            key={`comment-${send}`}
                                            onChange={(text) => {
                                                setComment(text);
                                            }}
                                            isFreeTextOptionEnabled={meterReading?.freeTextCommentEnabled}
                                        />
                                    </OptionalLabel>
                                )}

                                {showReadingHintRequiredWarning &&
                                    !features?.unplausibleReadingsImageRequired &&
                                    !features?.historicReadingsValidationRequired && (
                                        <OptionalLabel>
                                            <Information type={InformationType.ERROR}>{t('jobs.details.warnings.hintRequiredError')}</Information>
                                        </OptionalLabel>
                                    )}

                                {!showReadingHintRequiredWarning && !isCommentSetAndValid && comment && (
                                    <OptionalLabel>
                                        <Information type={InformationType.WARNING}>{t('jobs.details.warnings.hintMinLengthWarning')}</Information>
                                    </OptionalLabel>
                                )}

                                <CapturedValuesSection
                                    visibility={capturedValuesVisibility}
                                    phoneRequired={capturedValuesConfig?.phoneRequired === true}
                                    emailRequired={capturedValuesConfig?.emailRequired === true}
                                    defaultPhoneRegion={capturedValuesDefaultRegion}
                                    value={capturedValuesInput}
                                    onChange={setCapturedValuesInput}
                                    submitAttempted={capturedValuesSubmitAttempted}
                                    validationFeedbackDelay={meterReading?.validationFeedbackDelay}
                                />

                                <OptionalLabel className={'lg:mt-0 mt-2'}>
                                    {features.imageAlwaysRequired && (
                                        <p className={'description'}>
                                            {t('jobs.details.warnings.imageAlwaysRequiredHint', {
                                                count: job.meter.meterRateTasks.length,
                                            })}
                                        </p>
                                    )}
                                    <PopConfirmButton
                                        primary={true}
                                        block={true}
                                        disabled={sendButtonDisabled}
                                        isConfirmed={!hasWarnings && isValid}
                                        onClick={() => {
                                            let results: MeterTaskResultType[];

                                            try {
                                                results = Object.entries(resultState.values)
                                                    .map(([taskId, reading]) => {
                                                        let r = reading as ResultStateValueType;
                                                        if (r.readingValue && r.valid) {
                                                            return {
                                                                taskId: taskId,
                                                                comment: comment,
                                                                readingValue: Number.parseFloat(r.readingValue),
                                                                readingImages: r.images.map((i) => ({
                                                                    ...toReadingImagePayload(i.imageBase64),
                                                                    aiResult: i.remoteResult?.meta,
                                                                })),
                                                            } as MeterTaskResultType;
                                                        }
                                                        return undefined;
                                                    })
                                                    .filter((item): item is MeterTaskResultType => !!item);
                                            } catch (err) {
                                                if (err instanceof InvalidReadingImagePayloadError) {
                                                    dispatch(
                                                        notificationActions.showErrorNotification(
                                                            t('jobs.details.notifications.imageFormatUnsupported.title'),
                                                            t('jobs.details.notifications.imageFormatUnsupported.message'),
                                                        ),
                                                    );
                                                    return;
                                                }

                                                throw err;
                                            }

                                            submittedSnapshotRef.current = unsavedChangesSnapshotStringRef.current;

                                            const capturedValues = buildCapturedValuesPayload({
                                                visibility: capturedValuesVisibility,
                                                input: capturedValuesInput,
                                                defaultPhoneRegion: capturedValuesDefaultRegion,
                                            });

                                            setSubmittedReadingSnapshot({
                                                readingDate: readingDate.toISOString(),
                                                results,
                                            });
                                            setSubmitFeedbackStatus('sending');
                                            dispatch(jobsActions.sendMeterTaskResults(jobId, readingDate.toISOString(), results, capturedValues));

                                            setIsInputCollapseOpen(false);

                                            setSent(send + 1);
                                        }}
                                        okText={t('jobs.details.send.confirm')}
                                        cancelText={t('jobs.details.send.cancel')}
                                        title={hasWarnings ? t('jobs.details.warnings.check.title') : t('jobs.details.warnings.incomplete.title')}
                                        content={
                                            isValid ? (
                                                <>
                                                    <p className={'text-gray-500 font-medium'}>
                                                        {t('jobs.details.warnings.check.message')}
                                                        <br />
                                                        <br />
                                                    </p>
                                                    <p>{t('jobs.details.warnings.check.details')}</p>
                                                </>
                                            ) : (
                                                <>
                                                    <p className={'text-gray-500 font-medium'}>
                                                        {t('jobs.details.warnings.incomplete.message')}
                                                        <br />
                                                        <br />
                                                    </p>
                                                    <p>{t('jobs.details.warnings.incomplete.details')}</p>
                                                </>
                                            )
                                        }
                                    >
                                        {t('jobs.details.buttons.send')}
                                    </PopConfirmButton>
                                </OptionalLabel>
                            </LabeledList>
                        </Collapsable>
                    </Card>
                </LoadingOverlay>
            )}
        </BasicScreen>
    );
};

/**
 * Behandelt den Auftrag, der nicht (mehr) im Store liegt (#336): serverseitig storniert, oder beim
 * Deep-Link noch nicht geladen. Die Unterscheidung braucht einen eigenen Baustein, weil
 * JobDetailsContent den Auftrag in Dutzenden Hooks voraussetzt -- ein Guard am Ende ihrer
 * Renderfunktion kommt zu spät, da alle Hooks vorher laufen.
 *
 * Im Zustand 'loading' wird bewusst nichts gerendert statt eines eigenen Spinners: Daneben steht
 * die Auftragsliste, und ein zusätzlicher Ladehinweis in der Detailspalte bräuchte einen neuen
 * i18n-Key für einen Moment, der in der Regel unsichtbar ist.
 */
const JobDetails = ({ jobId, className }: { jobId: string; className?: string }) => {
    const dispatch = useDispatch();

    const jobByIdSelector = useMemo(() => jobsSelectors.getJobById(jobId), [jobId]);
    const job = useSelector(jobByIdSelector);
    const hasLoadedJobs = useSelector(jobsSelectors.hasLoadedJobs);
    const isRefreshing = useSelector(jobsSelectors.isLoadingBlocked);

    const detailsState = resolveJobDetailsState({ hasJob: !!job, hasLoadedJobs, isRefreshing });

    useEffect(() => {
        if (detailsState === 'missing') {
            dispatch(push('/ablesung'));
        }
    }, [detailsState, dispatch]);

    if (detailsState !== 'ready') {
        return null;
    }

    return <JobDetailsContent jobId={jobId} className={className} />;
};

export default JobDetails;
